import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';

const RetailCartContext = createContext();
export const useRetailCart = () => useContext(RetailCartContext);

/**
 * Carrito de la tienda MINORISTA (separado del mayorista).
 * Usa el precio minorista del producto y persiste en localStorage.
 */
export function RetailCartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('msf_retail_cart')) || [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('msf_retail_cart', JSON.stringify(items));
    } catch {
      /* no-op: modo privado o storage lleno */
    }
  }, [items]);

  const keyOf = (it) => `${it.product}-${it.variantId || ''}`;

  const addItem = (product, { quantity = 1, variant = null } = {}) => {
    // El precio minorista vive en la variante (con respaldo al del producto).
    const price = variant?.retailPrice || product.retailPrice;
    if (!(price > 0)) return toast.error('Este producto todavía no está a la venta online');
    setItems((prev) => {
      const newItem = {
        product: product._id,
        slug: product.slug,
        name: product.name,
        image: variant?.image || product.images?.[0] || '',
        variantId: variant?._id || null,
        sku: variant?.sku || '',
        price,
        quantity,
        color: variant?.color || '',
        size: variant?.size || '',
      };
      const idx = prev.findIndex((i) => keyOf(i) === keyOf(newItem));
      if (idx >= 0) {
        const copy = [...prev];
        copy[idx] = { ...copy[idx], quantity: copy[idx].quantity + quantity };
        return copy;
      }
      return [...prev, newItem];
    });
    toast.success('Agregado al carrito');
  };

  const removeItem = (key) => setItems((prev) => prev.filter((i) => keyOf(i) !== key));
  const updateQty = (key, qty) =>
    setItems((prev) => prev.map((i) => (keyOf(i) === key ? { ...i, quantity: Math.max(1, qty) } : i)));
  const clearCart = () => setItems([]);

  const subtotal = useMemo(() => items.reduce((s, i) => s + i.price * i.quantity, 0), [items]);
  const count = useMemo(() => items.reduce((s, i) => s + i.quantity, 0), [items]);

  return (
    <RetailCartContext.Provider
      value={{ items, count, subtotal, addItem, removeItem, updateQty, clearCart, keyOf }}
    >
      {children}
    </RetailCartContext.Provider>
  );
}
